import { LocalizedTypography } from '@mews-commander/ui';
import { TextKey, ArticlePath, createGuideLink, DataTestId } from '@mews-commander/enums';
import { Banner, Button, Stack, Link, LinkButton } from '@optimus-web/core';
import { Date, useDateTimeLocalizationInfo, LocalizedText, DateService, showSuccessAlert, lt } from '@mews/framework';
import { useMutateMulticurrencyEarlyOptIn } from '../hooks/useMutateMulticurrencyEarlyOptIn';
import { useMutateMulticurrencyConfiguration } from '../hooks/useMutateMulticurrencyConfiguration';

interface DescriptionSectionProps {
    isMulticurrencyServiceEnabled: boolean;
    isDccMulticurrencyServiceEnabled: boolean;
    isFxMulticurrencyServiceEnabled: boolean;
    isDccMulticurrencyServiceAvailable: boolean;
    multicurrencyStartDate: Date | null;
    isFetching: boolean;
}

export const DescriptionSection = ({
    isMulticurrencyServiceEnabled,
    isDccMulticurrencyServiceEnabled,
    isFxMulticurrencyServiceEnabled,
    isDccMulticurrencyServiceAvailable,
    multicurrencyStartDate,
    isFetching,
}: DescriptionSectionProps) => {
    const localizationInfo = useDateTimeLocalizationInfo();
    const { mutate: mutateEarlyOptIn } = useMutateMulticurrencyEarlyOptIn();
    const { mutate: mutateConfiguration } = useMutateMulticurrencyConfiguration();

    const guideLink = createGuideLink(ArticlePath.MulticurrencyByMewsPaymentsFAQ);

    const activateMulticurrencyService = () => {
        mutateConfiguration(
            {
                isFxMultiCurrencyServiceEnabled: true,
                isDccMultiCurrencyServiceEnabled: isDccMulticurrencyServiceAvailable,
            },
            {
                onSuccess: () => {
                    showSuccessAlert({
                        description: lt(
                            isDccMulticurrencyServiceAvailable
                                ? 'MulticurrencySettingsAlertEarlyAccessActivatedBoth'
                                : 'MulticurrencySettingsAlertEarlyAccessActivatedSingle'
                        ),
                    });
                },
            }
        );
    };

    const handleClickPrimaryButton = () => {
        mutateEarlyOptIn(
            {},
            {
                onSuccess: () => {
                    if (isFxMulticurrencyServiceEnabled && isDccMulticurrencyServiceEnabled) {
                        showSuccessAlert({
                            description: lt('MulticurrencySettingsAlertEarlyAccessActivatedBoth'),
                        });
                    } else if (isFxMulticurrencyServiceEnabled || isDccMulticurrencyServiceEnabled) {
                        showSuccessAlert({
                            description: lt('MulticurrencySettingsAlertEarlyAccessActivatedSingle'),
                        });
                    } else {
                        activateMulticurrencyService();
                    }
                },
            }
        );
    };

    return (
        <Stack vertical spacing="250">
            <LocalizedTypography
                as="h2"
                textKey={TextKey.MulticurrencySettingsPageTitle}
                textStyle="titleMediumStrong"
            />

            <LocalizedTypography as="p" textKey={TextKey.MulticurrencySettingsDescription} textStyle="bodyLarge" />

            {isMulticurrencyServiceEnabled && (
                <Link
                    data-test-id={DataTestId.MulticurrencySettingsLinkLearnMore}
                    href={guideLink}
                    target="_blank"
                    rel="noreferrer"
                >
                    <LocalizedText textKey={TextKey.MulticurrencySettingsLinkTextLearnMore} />
                </Link>
            )}

            {multicurrencyStartDate && !isMulticurrencyServiceEnabled && (
                <Banner
                    primaryText={
                        <LocalizedText
                            textKey={TextKey.MulticurrencySettingsBannerPrimaryText}
                            substitutes={{
                                date: DateService.toLongString(multicurrencyStartDate, localizationInfo),
                            }}
                        />
                    }
                    secondaryText={
                        <Stack vertical spacing="150">
                            <LocalizedText textKey={TextKey.MulticurrencySettingsBannerSecondaryText} />

                            <Stack spacing="200">
                                <Button
                                    data-test-id={DataTestId.MulticurrencySettingsBannerButtonOptIn}
                                    disabled={isFetching}
                                    onClick={handleClickPrimaryButton}
                                    type="button"
                                    variant="primary"
                                >
                                    <LocalizedText textKey={TextKey.MulticurrencySettingsBannerButtonTextOptIn} />
                                </Button>

                                <LinkButton
                                    data-test-id={DataTestId.MulticurrencySettingsBannerButtonLearnMore}
                                    href={guideLink}
                                    target="_blank"
                                    variant="secondary"
                                >
                                    <LocalizedText textKey={TextKey.MulticurrencySettingsBannerSecondaryButtonText} />
                                </LinkButton>
                            </Stack>
                        </Stack>
                    }
                    variant="info"
                />
            )}
        </Stack>
    );
};
